import { useId, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import type {
  PremiseConfig,
  SchemeBinding,
  SchemeContent,
  SchemeKnobValues,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { hasPromotionsLicense, hasStreamingTvLicense } from "@shared/products";
import LivePreview from "../template/LivePreview";
import TemplateSubEditor, {
  type TemplateApps,
} from "../template/TemplateSubEditor";
import { moveZone, type ScreenRow } from "../template/screensModel";
import { tokens } from "../../theme";
import { AppsPanel, RadioPanel } from "./CataloguePanels";
import { FieldBlock, PlacementChoice, SegmentedChoice } from "./StartNavLayout";
import TvPlayerSetting from "./TvPlayerSetting";
import {
  INFO_OWN_PATH,
  INFO_STANDARD_PATH,
  TV_ITEM,
  menuZones,
  screenIdOf,
  standardInfoSections,
  type MenuItem,
} from "./menuItems";
import { resolveLocalized } from "@shared/i18n";

/** Where a menu item's content is edited, when it is not edited here. */
const ELSEWHERE = {
  vod: { to: "/streaming-tv/menu", label: "menuItemDrawer.elsewhere.vod" },
  guide: {
    to: "/streaming-tv/channels",
    label: "menuItemDrawer.elsewhere.guide",
  },
  offers: {
    to: "/promotions/offers",
    label: "menuItemDrawer.elsewhere.offers",
  },
} as const;
const elsewhereFor = (id: string) =>
  id in ELSEWHERE ? ELSEWHERE[id as keyof typeof ELSEWHERE] : undefined;

/** Items the TV opens as a screen of their own - the preview opens there. */
const NO_SCREEN = new Set([TV_ITEM, "cast"]);

interface Props {
  /** The item whose drawer is open; null = closed. */
  item: MenuItem | null;
  /** The premise as the page shows it (its binding already materialised). */
  premise: PremiseConfig;
  icon?: ReactNode;
  apps: TemplateApps;
  onClose: () => void;
  /**
   * Persist the edited premise and toast the outcome. Resolves true when it is
   * stored; false leaves the drawer open with the edits intact (the failure has
   * already been toasted).
   */
  onSave: (next: PremiseConfig) => Promise<boolean>;
  /**
   * Leave the drawer for a page of its own (Information → Own sections /
   * Standard sections). The drawer's unsaved edits travel along in `next`.
   */
  onOpenPage?: (next: PremiseConfig, to: string) => void;
}

/**
 * The right-hand drawer for ONE start-screen menu item (NendaPortal_1
 * 1677:13245): what it is, how it looks on the TV, whether it is shown, and its
 * content.
 *
 * Edits happen on a local copy of the premise. Cancel, the X and the backdrop
 * throw it away; Save hands it to the page, which persists it, toasts the
 * outcome and closes the drawer only when it worked.
 */
export default function MenuItemDrawer(props: Props) {
  const { item, onClose } = props;
  const titleId = useId();
  return (
    <Drawer
      anchor="right"
      open={!!item}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": titleId,
        sx: {
          width: { xs: "100%", md: 699 },
          borderRadius: { xs: 0, md: "16px 0 0 16px" },
          display: "flex",
          flexDirection: "column",
        },
      }}
      data-testid="menu-item-drawer"
    >
      {item && (
        // Keyed by item so each opening starts from the page's premise.
        <DrawerBody key={item.id} {...props} item={item} titleId={titleId} />
      )}
    </Drawer>
  );
}

function DrawerBody({
  item,
  premise,
  icon,
  apps,
  onClose,
  onSave,
  onOpenPage,
  titleId,
}: Props & { item: MenuItem; titleId: string }) {
  const { t } = useTranslation(["templates", "common"]);
  const [local, setLocal] = useState<PremiseConfig>(premise);
  const [saving, setSaving] = useState(false);

  const binding = local.scheme as SchemeBinding;
  const manifest = SCHEME_MANIFESTS[binding.key];
  const knobs = binding.knobs ?? {};
  const setKnobs = (next: SchemeKnobValues) =>
    setLocal({ ...local, scheme: { ...binding, knobs: next } });
  const setContent = (content: SchemeContent) =>
    setLocal({ ...local, scheme: { ...binding, content } });

  // Re-read from the LOCAL knobs, so the switch and title follow each edit.
  const zones = menuZones(
    {
      manifest,
      knobs: binding.knobs,
      hasPromotions: hasPromotionsLicense(local.products),
      hasStreamingTv: hasStreamingTvLicense(local.products),
      appsCertified: apps.certifiedOn.size > 0,
      certificationNote: apps.certNote,
    },
    t
  );
  const idOf = (r: ScreenRow) => screenIdOf(manifest, r);
  const row = item.fixed
    ? undefined
    : (zones.home.find((r) => idOf(r) === item.id) ??
      zones.info.find((r) => idOf(r) === item.id));
  const insideInfo = !!row && zones.info.includes(row);

  const save = async () => {
    if (JSON.stringify(local) === JSON.stringify(premise)) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave(local);
    } finally {
      setSaving(false);
    }
  };

  const loc = {
    supportedLocales: local.supportedLocales,
    defaultLocale: local.defaultLocale,
  };
  const elsewhere = elsewhereFor(item.id);

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2.5, md: "33px" },
          pt: { xs: 2.5, md: "32px" },
          pb: 4,
        }}
      >
        <Stack direction="row" spacing="22px" alignItems="flex-start">
          <Box
            aria-hidden
            sx={{
              width: 50,
              height: 50,
              flexShrink: 0,
              borderRadius: "8px",
              bgcolor: tokens.brandTintSoft,
              color: tokens.brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                typography: "headlineLg",
                fontWeight: "fontWeightSemibold",
                lineHeight: "normal",
                color: tokens.ink,
              }}
            >
              {item.name}
            </Typography>
            {item.description && (
              <Typography
                variant="body2"
                sx={{
                  mt: "6px",
                  maxWidth: 466,
                  lineHeight: "20px",
                  color: tokens.textSecondary,
                }}
              >
                {item.description}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label={t("common:buttons.close")}
            onClick={onClose}
            sx={{ mt: "-4px", color: tokens.textSecondary }}
            data-testid="menu-item-drawer-close"
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Stack>

        <Divider sx={{ mt: "30px", mb: "31px", borderColor: tokens.line }} />

        <DrawerHeading
          title={t("startNavLayout.preview")}
          subtitle={t("menuItemDrawer.previewSubtitle", { name: item.name })}
        />
        <Box
          sx={{
            mt: "33px",
            mb: "32px",
            "& iframe, & > *": { borderRadius: "8px" },
          }}
        >
          <LivePreview
            premise={local}
            screen={
              NO_SCREEN.has(item.id) ? undefined : insideInfo ? "info" : item.id
            }
            maxWidth={636}
            hideLabel
          />
        </Box>

        {row && (
          <ShownRow
            title={
              insideInfo
                ? t("menuItemDrawer.shownInfo")
                : t("menuItemDrawer.shownStart")
            }
            help={
              insideInfo
                ? t("menuItemDrawer.helpInfo", { name: item.name })
                : t("menuItemDrawer.helpStart", { name: item.name })
            }
            on={row.fixedNote ? true : row.on}
            fixedNote={row.fixedNote}
            onChange={
              row.knobId
                ? (on) => setKnobs({ ...knobs, [row.knobId!]: on })
                : undefined
            }
          />
        )}

        {/* A screen the operator names. Own page carries its own name field. */}
        {row?.title && item.id !== "custom" && (
          <FieldBlock label={t("common:labels.title")} help={row.title.help}>
            <TextField
              size="small"
              fullWidth
              value={row.title.raw}
              placeholder={row.title.placeholder}
              inputProps={{
                maxLength: row.title.maxLength,
                "aria-label": t("menuItemDrawer.titleAria", {
                  name: item.name,
                }),
              }}
              onChange={(e) =>
                setKnobs({ ...knobs, [row.title!.knobId]: e.target.value })
              }
              data-testid="menu-item-title-field"
            />
          </FieldBlock>
        )}

        {row?.moveTo && row.orderKnobId && (
          <PlacementChoice
            insideInfo={insideInfo}
            onMove={() => {
              const patch = moveZone(
                manifest,
                knobs,
                row.key,
                row.moveTo!.orderKnobId
              );
              if (patch) setKnobs(patch);
            }}
          />
        )}

        {item.id === TV_ITEM ? (
          <TvPlayerSetting
            premise={local}
            onChange={(tvPlayer) => setLocal({ ...local, tvPlayer })}
          />
        ) : elsewhere ? (
          <Box>
            {item.manageable && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {item.content}
              </Typography>
            )}
            <Button
              component={RouterLink}
              to={elsewhere.to}
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: "none" }}
              data-testid="menu-item-elsewhere"
            >
              {t(elsewhere.label)}
            </Button>
          </Box>
        ) : item.id === "apps" ? (
          <AppsPanel
            content={binding.content}
            {...loc}
            global={apps.apps}
            isCare={apps.isCare}
            onChange={setContent}
          />
        ) : item.id === "radio" ? (
          <RadioPanel
            content={binding.content}
            {...loc}
            onChange={setContent}
          />
        ) : item.id === "info" ? (
          <InfoSummary
            local={local}
            standard={standardInfoSections(t).map((s) => ({
              ...s,
              // Undefined when the template has no such section (Atelier,
              // Mosaic, Spectrum): counted as not shown.
              row: zones.info.find((r) => idOf(r) === s.id),
            }))}
            onOpen={(to) => onOpenPage?.(local, to)}
          />
        ) : row?.sub && item.manageable ? (
          <TemplateSubEditor
            premise={local}
            sub={row.sub}
            onChange={setLocal}
            apps={apps}
            stateBar={false}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          px: { xs: 2.5, md: "33px" },
          pt: 2,
          pb: { xs: 2.5, md: "32px" },
          bgcolor: tokens.surface,
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              width: 160,
              height: 40,
              borderRadius: "8px",
              borderColor: tokens.lineInput,
              color: tokens.inkSoft,
              typography: "bodyMd",
              fontWeight: "fontWeightSemibold",
              textTransform: "none",
              "&:hover": {
                borderColor: tokens.lineInput,
                bgcolor: tokens.rowAlt,
              },
            }}
            data-testid="menu-item-cancel"
          >
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void save()}
            disabled={saving}
            sx={{
              width: 180,
              height: 40,
              borderRadius: "8px",
              bgcolor: tokens.brand,
              typography: "bodyMd",
              fontWeight: "fontWeightSemibold",
              textTransform: "none",
            }}
            data-testid="menu-item-save"
          >
            {saving ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </Stack>
      </Box>
    </>
  );
}

/**
 * Information's drawer (NendaPortal_1 1689:13962): no editor of its own - a
 * summary of its two kinds of section, each with a way into its page.
 */
function InfoSummary({
  local,
  standard,
  onOpen,
}: {
  local: PremiseConfig;
  standard: { id: string; name: string; row?: ScreenRow }[];
  onOpen: (to: string) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const content = (local.scheme as SchemeBinding).content;
  const own = content.infoSections ?? [];
  const ownNames = own
    .map((s) =>
      resolveLocalized(
        s.section,
        local.defaultLocale,
        local.defaultLocale
      ).trim()
    )
    .filter(Boolean);
  const shown = standard.filter((s) =>
    s.row?.fixedNote ? true : s.row?.on
  ).length;
  return (
    <Stack spacing="64px" sx={{ mt: "48px" }}>
      <SummaryCard
        title={t("informationPage.ownSections")}
        subtitle={t("menuItemDrawer.ownSubtitle")}
        count={t("menuItems.sectionCount", { count: own.length })}
        names={ownNames.join(", ") || t("menuItemDrawer.noSections")}
        action={t("menuItemDrawer.editSections")}
        onAction={() => onOpen(INFO_OWN_PATH)}
        testId="info-summary-own"
      />
      <SummaryCard
        title={t("informationPage.standardSections")}
        subtitle={t("menuItemDrawer.standardSubtitle")}
        count={t("menuItemDrawer.standardShown", {
          shown,
          total: standard.length,
        })}
        names={standard.map((s) => s.name).join(", ")}
        action={t("menuItemDrawer.editStandard")}
        onAction={() => onOpen(INFO_STANDARD_PATH)}
        testId="info-summary-standard"
      />
    </Stack>
  );
}

function SummaryCard({
  title,
  subtitle,
  count,
  names,
  action,
  onAction,
  testId,
}: {
  title: string;
  subtitle: string;
  count: string;
  names: string;
  action: string;
  onAction: () => void;
  testId: string;
}) {
  return (
    <Box data-testid={testId}>
      <DrawerHeading title={title} subtitle={subtitle} gap="2px" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: "24px" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              typography: "titleSm",
              fontWeight: "fontWeightSemibold",
              lineHeight: 1.4,
              letterSpacing: "0.32px",
              color: tokens.ink,
            }}
          >
            {count}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: "4px", lineHeight: "20px", color: tokens.textSecondary }}
          >
            {names}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onAction}
          sx={{
            flexShrink: 0,
            height: 40,
            px: "31px",
            borderRadius: "8px",
            borderColor: tokens.brand,
            color: tokens.brand,
            bgcolor: tokens.surface,
            typography: "bodyMd",
            fontWeight: "fontWeightSemibold",
            textTransform: "none",
            "&:hover": {
              borderColor: tokens.brand,
              bgcolor: tokens.brandTintSoft,
            },
          }}
          data-testid={`${testId}-edit`}
        >
          {action}
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * A drawer section heading (NendaPortal_1): 20px semibold, 0.4px tracking,
 * over a 14px secondary line. Larger than the pages' PanelHeading on purpose.
 */
function DrawerHeading({
  title,
  subtitle,
  gap = "10px",
}: {
  title: string;
  subtitle?: string;
  gap?: string;
}) {
  return (
    <Box>
      <Typography
        component="h3"
        sx={{
          typography: "titleLg",
          fontWeight: "fontWeightSemibold",
          lineHeight: 1.4,
          letterSpacing: "0.4px",
          color: tokens.ink,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          sx={{ mt: gap, lineHeight: "20px", color: tokens.textSecondary }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/** "Shown in the start view": heading, one line of help, Yes/No on the right. */
function ShownRow({
  title,
  help,
  on,
  fixedNote,
  onChange,
}: {
  title: string;
  help: string;
  on: boolean;
  fixedNote?: string;
  onChange?: (on: boolean) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  return (
    <Box sx={{ mb: "48px" }} data-testid="menu-item-shown">
      <Typography
        component="h3"
        sx={{
          typography: "titleLg",
          fontWeight: "fontWeightSemibold",
          lineHeight: 1.4,
          letterSpacing: "0.4px",
          color: tokens.ink,
        }}
      >
        {title}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: "48px" }}
        sx={{ mt: "4px" }}
      >
        <Typography
          variant="body2"
          sx={{ lineHeight: "20px", color: tokens.textSecondary }}
        >
          {help}
        </Typography>
        {onChange ? (
          <SegmentedChoice
            value={on ? "yes" : "no"}
            options={[
              { value: "yes", label: t("common:buttons.yes") },
              { value: "no", label: t("common:buttons.no") },
            ]}
            onChange={(v) => onChange(v === "yes")}
            testId="startnav-active"
          />
        ) : (
          <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
            {fixedNote ?? t("startNavLayout.alwaysShown")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
